/**
 * File-input helpers for the two ways a report photo can arrive.
 *
 * **Camera** — shoot now. The photo is taken on site, so its location is
 * whatever `navigator.geolocation` reports at that moment; that is the
 * authoritative stamp and needs no metadata.
 *
 * **Gallery** — pick an existing photo. Its location can only come from EXIF,
 * and on Android that is frequently unavailable: the OS blanks the GPS tags
 * (every rational `0/0`) before the bytes reach the browser, so no web-side
 * code — server-side PIL or a client-side EXIF library — can recover them.
 * Leaving `accept` unset on Android opens the Documents UI rather than the
 * media picker, which is the only path with any chance of unredacted bytes
 * (notably a file-manager copy, which is a plain document rather than a
 * MediaStore image). It is best-effort, not a guarantee.
 */

/**
 * True on Android. Deliberately a UA sniff: the behaviour being worked around
 * is an Android-platform one with no feature-detectable surface. A wrong answer
 * only changes which picker opens — the caller validates the file regardless.
 */
export const isAndroid = (): boolean => /Android/i.test(navigator.userAgent);

/** Where a picked photo came from — decides how its location is resolved. */
export type PhotoSource = 'camera' | 'gallery';

/**
 * A photo the user just picked, with how it arrived.
 *
 * One object rather than positional arguments: the source travels from the
 * picker, through the template-preview render tree, to whoever resolves the
 * photo's metadata. Widening a field here does not re-touch every signature
 * along that path.
 */
export interface PhotoPick {
  file: File;
  source: PhotoSource;
}

/**
 * `accept` for the gallery input — `undefined` on Android (see the module
 * note), `image/*` elsewhere.
 *
 * Callers MUST run {@link isImageFile} on the result: with `accept` unset the
 * user can pick anything at all.
 */
export const galleryAccept = (): string | undefined => (isAndroid() ? undefined : 'image/*');

/**
 * Extensions treated as images when the picker is unfiltered. Some Android
 * document providers report a generic `application/octet-stream` for a real
 * photo, so the extension is a fallback — never the primary check.
 */
const IMAGE_EXTENSIONS: readonly string[] = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'heic',
  'heif',
  'gif',
  'bmp',
];

/** True when `file` looks like an image the photo endpoint can process. */
export const isImageFile = (file: File): boolean => {
  if (file.type.startsWith('image/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_EXTENSIONS.includes(ext);
};
